import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  ApplicationTitle,
  ButtonGroupComponent,
  NewFeatureTooltip,
  PlacementEnum,
  Toast,
} from '@getvim/atomic-ui';
import { themes, ThemeVariablesWrapper } from '@getvim/components-hooks-use-theme';
import { useResourcesWithPII, useVimAuth, useApi } from '@getvim/internal-vim-os-sdk/react';
import { ActivationStatus, PatientWithPII } from '@getvim/internal-vim-os-sdk/types';
import { setGlobalAccessToken } from '@getvim/vim-connect/src/infra/common/setGlobalAccessToken';
import { useEnrichLoggerMetadata } from '../hooks/useEnrichLoggerMetadata';
import { createLogger } from '../utils/createLogger';
import { usePatientLookupSharedService } from '../hooks/usePatientLookupSharedService';
import { RedirectOrigin, Tab } from '../types';
import { useAnalytics } from '../analytics/AnalyticsProvider';
import TabsContainer from './TabsContainer';
import '@getvim/atomic-ui/assets/styles/main.less';
import './App.less';
import { graphQlClient } from '../utils/graphQlClient';

const { Slide, ToastContainer } = Toast;

const logger = createLogger('mainApp');

const optionalTabs = [Tab.PriorAuthInquiry, Tab.SubmitCase];
const displayNewFeatureTooltip = true;

const App = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<Tab>(Tab.PriorAuthInquiry);
  useEnrichLoggerMetadata();
  const { accessToken } = useVimAuth();
  useMemo(() => {
    if (accessToken) {
      setGlobalAccessToken(accessToken);
      graphQlClient.setAccessToken(accessToken);
    }
  }, [accessToken]);

  const { analyticsClient } = useAnalytics();

  const { ehrState } = useResourcesWithPII();
  const { setActivationStatus } = useApi();

  const { patient: ehrPatient } = ehrState;
  const [patient, isPatientEligible] = usePatientLookupSharedService(ehrPatient as PatientWithPII);

  const goToTab = useCallback((tab: Tab) => {
    setCurrentTab(tab);
  }, []);

  useEffect(() => {
    if (patient && isPatientEligible && !isActive) {
      logger.info('prior auth app activated', {
        noPHI: true,
        isPatientEligible,
        patientId: patient?.identifiers?.vimPatientId,
      });
      setActivationStatus(ActivationStatus.ENABLED);
      setIsActive(true);
    } else if ((!patient || !isPatientEligible) && isActive) {
      logger.info('prior auth app deactivated', {
        noPHI: true,
        isPatientEligible,
        patientId: ehrPatient?.identifiers?.vimPatientId,
      });
      setActivationStatus(ActivationStatus.DISABLED);
      setIsActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, isActive, isPatientEligible]);

  return (
    <ThemeVariablesWrapper theme={themes.vim}>
      <div>
        <ApplicationTitle title="Prior Auth" />
        <div className="app-content">
          <NewFeatureTooltip
            className="block"
            placement={PlacementEnum.TOP}
            showCircleAnimation={false}
            tooltipContent={
              <>
                <strong>New!</strong> Click to submit Prior Auth
              </>
            }
            visible={displayNewFeatureTooltip}
          >
            <ButtonGroupComponent
              items={optionalTabs.map((tab) => ({
                value: tab,
                text: tab,
              }))}
              bgColor="none"
              buttonType="tab"
              className="tabs-buttons-group"
              direction="row"
              value={currentTab}
              onChange={(selectedTab: Tab) => {
                logger.info('prior auth tab clicked', {
                  tab: selectedTab,
                  noPHI: true,
                });
                setCurrentTab(selectedTab);
                analyticsClient.track('prior_auth_tab_switch', {
                  tabName: selectedTab,
                  redirected_from: RedirectOrigin.APP_TAB,
                });
              }}
              style={{ fontWeight: 'bold', marginBottom: '20px' }}
            />
          </NewFeatureTooltip>
          {isPatientEligible && patient && (
            <TabsContainer patient={patient} goToTab={goToTab} currentTab={currentTab} />
          )}
        </div>
        <ToastContainer
          className=""
          position="bottom-right"
          hideProgressBar
          autoClose={3000}
          transition={Slide}
        />
      </div>
    </ThemeVariablesWrapper>
  );
};

export default App;
